import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, throwError } from 'rxjs';
import { buildApiCallBody, buildApiCallParams, restoreApiCallConfig } from '../../dvm-layout-builder/dvm-input-api-call.utils';

/** Requests the data for one chart/table; configuration travels with its saved payload. */
@Injectable({ providedIn: 'root' })
export class DesignWidgetApiService {
  private readonly http = inject(HttpClient);

  resolve(fallbackUrl: string, payload: Record<string, unknown>, chartType?: string): Observable<Record<string, unknown>> {
    const config = restoreApiCallConfig(payload['customApi']);
    const { customApi: _config, ...definition } = payload;
    if (!config?.enabled) return this.http.post<Record<string, unknown>>(fallbackUrl, definition);
    if (!config.url.trim()) return throwError(() => new Error('Enter a custom API URL.'));
    try { if (config.method !== 'GET') JSON.parse(config.bodyTemplate || '{}'); }
    catch { return throwError(() => new Error('Request body must be valid JSON.')); }
    const tokens = { datasets: definition['datasets'] ?? (definition['dataset'] ? [definition['dataset']] : []),
      filters: [], ...definition, chartType: chartType ?? definition['chartType'], payload: definition };
    let options;
    try { options = config.method === 'GET'
      ? { params: buildApiCallParams(Object.fromEntries(Object.entries(tokens).map(([key, value]) =>
          [key, value !== null && typeof value === 'object' ? JSON.stringify(value) : value]))) }
      : { body: buildApiCallBody(config.bodyTemplate, tokens, true) }; }
    catch { return throwError(() => new Error('Request body must be valid JSON after token substitution.')); }
    const componentId = String(payload['componentId'] ?? '');
    const custom = this.http.request<Record<string, unknown>>(config.method, config.url, options).pipe(map(response => {
      const addressed = Array.isArray(response['results'])
        ? { ...response, results: (response['results'] as Array<Record<string, unknown>>).filter(entry => String(entry['componentId']) === componentId) }
        : response;
      if (config.mode !== 'parallel' && !widgetApiResult(addressed, componentId)) {
        throw new Error('Custom API returned no result for this componentId.');
      }
      return addressed;
    }));
    if (config.mode !== 'parallel') return custom;
    return forkJoin([custom, this.http.post<Record<string, unknown>>(fallbackUrl, definition)]).pipe(
      map(([response, normal]) => {
        const customResult = widgetApiResult(response, String(payload['componentId'] ?? ''));
        return customResult ? response : normal;
      }),
    );
  }
}

/** An envelope is addressed by componentId; direct compute data is accepted too. */
export function widgetApiResult(response: Record<string, unknown> | null, componentId: string): Record<string, unknown> | null {
  if (!response) return null;
  if (!Array.isArray(response['results'])) {
    if (response['componentId'] && String(response['componentId']) !== componentId) return null;
    return response['result'] && typeof response['result'] === 'object' ? response['result'] as Record<string, unknown> : response;
  }
  const entry = (response['results'] as Array<Record<string, unknown>>).find(item => String(item['componentId']) === componentId);
  return entry?.['result'] && typeof entry['result'] === 'object' ? entry['result'] as Record<string, unknown> : null;
}
